import { SkyButton, SkyMessageBar, SkyText } from "@skypointcloud/skypoint-ui";
import type { RunInstanceLink } from "@/components/runs/RunInstanceSwitchNotice";

/**
 * A link from an AI app (ADR 0009, release 8B) names the instance its write or its draft
 * connection belongs to: `/mcp-approve/<id>?instance=<id>` and `/connections/<id>/setup?instance=<id>`.
 * The server reads the instance only from the request header, so a link never switches on its
 * own (`?instance=` never selects, `TenantInstanceContext`): once the person's reach to that
 * instance is proven (it is in `GET /me` with the page's permission), this offers "Switch", and
 * nothing of the record is read under the wrong instance in the meantime.
 */
export function McpInstanceSwitchNotice({
  link,
  subject,
  onSwitch,
  testId,
}: {
  link: Extract<RunInstanceLink, { kind: "other" | "no_access" | "unresolved" }>;
  /** What the link is about, as a sentence's subject: "This approval", "This connection". */
  subject: string;
  onSwitch: (instanceId: string) => void;
  testId: string;
}) {
  if (link.kind === "unresolved") {
    return (
      <SkyText size={300} color="muted" testId={`${testId}-loading`}>
        Loading…
      </SkyText>
    );
  }
  if (link.kind === "no_access") {
    return (
      <SkyMessageBar
        intent="warning"
        layout="multiline"
        testId={testId}
        title="You cannot open the instance this link is for"
        message={`${subject} belongs to an instance you do not have access to. Sign in with the account you connected the AI app with.`}
      />
    );
  }
  const name = link.instance.name;
  return (
    <SkyMessageBar
      intent="info"
      layout="multiline"
      testId={testId}
      message={`${subject} belongs to ${name} (${link.instance.tenantDisplayName}). Switch to ${name} to open it.`}
      actions={
        <SkyButton
          appearance="secondary"
          size="small"
          testId={`${testId}-switch`}
          onClick={() => onSwitch(link.instance.id)}
        >
          Switch
        </SkyButton>
      }
    />
  );
}
